/**
 * DeviceTerminal — the device Web CLI sheet (xterm.js over the controller's RTTY bridge).
 *
 * Design intent: an instrument window, not a toy console. The terminal well takes the page;
 * everything around it answers three operator questions without reading docs:
 *   1. Which box am I on?          -> name + serial + kind, always visible in the header.
 *   2. Is the shell live?          -> a status pill (the status ramp, operational state only).
 *   3. What will end this session? -> idle timeout / session limit + a running clock.
 * When no shell is possible, the well is covered by a plain-language reason from the server
 * (device offline, RTTY server misconfigured, device did not answer) and a Reconnect action.
 *
 * Tokens only: the xterm palette is read from the Meridian CSS variables at runtime and
 * re-applied when the theme flips, so light and dark are both first-class. Brand violet is
 * used for the cursor and the Reconnect affordance only. xterm ships from npm (no CDN).
 *
 * Keyboard: Escape and outside clicks do NOT close the sheet — Escape belongs to the shell
 * (vi, less). Close with the header button.
 */
import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Terminal, type ITheme } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";

import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { useDeviceTerminal, type TerminalPhase, type TerminalStatus } from "@/lib/terminal/useDeviceTerminal";
import type { HealthState } from "@/types/domain";

export interface TerminalDevice {
  serial: string;
  name?: string | null;
  kind?: string | null;
}

interface DeviceTerminalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  device: TerminalDevice;
}

const KIND_LABEL: Record<string, string> = {
  access_point: "Access point",
  gateway: "Gateway",
  switch: "Switch",
};

const PHASE_PILL: Record<TerminalPhase, { ramp: HealthState | "info"; label: string }> = {
  idle: { ramp: "unknown", label: "Not connected" },
  requesting: { ramp: "info", label: "Requesting shell" },
  connecting: { ramp: "info", label: "Opening relay" },
  waiting_device: { ramp: "info", label: "Waiting for device" },
  ready: { ramp: "healthy", label: "Connected" },
  closed: { ramp: "unknown", label: "Disconnected" },
  failed: { ramp: "critical", label: "Unavailable" },
};

/** What the operator can do about each refusal. Codes come from services/device_terminal. */
const HINTS: Record<string, string> = {
  device_offline: "The device must hold an owgw connection before a shell can open. Check its uplink and power.",
  device_timeout: "owgw reached the device but it did not answer. It may be rebooting or overloaded.",
  device_attach_timeout:
    "The device could not dial the RTTY server. The platform owner should check owgw RTTY_SERVER and port 5912 reachability.",
  rtty_closed_before_ready: "owgw dropped the session before the device attached. Try again.",
  rtty_connect_failed: "The controller could not reach owgw's RTTY viewer (port 5913).",
  rtty_disabled: "RTTY is switched off in owgw configuration.",
  rtty_capacity: "owgw is at its RTTY session limit. Close another terminal and retry.",
  rtty_misconfigured: "owgw offered an RTTY port this controller does not allow.",
  owgw_unreachable: "The owgw gateway service is not answering the controller.",
  owgw_unconfigured: "This controller has no owgw connection configured.",
  device_not_in_owgw: "owgw has no record of this device, so it cannot start a shell.",
  capacity: "Too many terminals are open on this controller right now.",
  handshake_refused: "Reload the page to refresh your login, then reconnect.",
  superseded: "You opened this device's terminal somewhere else; only one session per device is kept.",
  // OP#1594: the server closes an open shell once the operator's sign-in is revoked.
  signed_out: "Your sign-in ended (signed out, or your access changed). Reload the page and sign in again.",
  idle_timeout: "Reconnect to start a fresh session.",
  max_duration: "Reconnect to start a fresh session.",
};

/** "15 19 27" (a Meridian token triplet) -> "rgb(15, 19, 27)" (a color xterm can parse). */
function tokenColor(name: string, alpha?: number): string | undefined {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const parts = raw.split(/\s+/).map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return undefined;
  return alpha === undefined ? `rgb(${parts.join(", ")})` : `rgba(${parts.join(", ")}, ${alpha})`;
}

function themeFromTokens(): ITheme {
  return {
    background: tokenColor("--md-surface-2"),
    foreground: tokenColor("--md-ink"),
    cursor: tokenColor("--md-brand"),
    cursorAccent: tokenColor("--md-surface-2"),
    selectionBackground: tokenColor("--md-brand", 0.28),
  };
}

function fmtClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function DeviceTerminal({ open, onOpenChange, device }: DeviceTerminalProps) {
  // Callback-ref STATE, not useRef: the well mounts inside the dialog portal, so a ref
  // read once in an effect can still be null. Setting state re-runs the effect.
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);

  const session = useDeviceTerminal(device.serial, {
    onOutput: (bytes) => termRef.current?.write(bytes),
    onReady: () => termRef.current?.focus(),
  });
  // xterm callbacks are registered once per mount; read the latest session API through a ref.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // --- xterm lifecycle: one Terminal per open sheet -------------------------------------
  // The well lives inside Dialog.Portal > Dialog.Content, which mounts on its own schedule.
  // This effect used to read a plain ref and `return` when it was still null -- and nothing
  // ever retried, so the Terminal was never created: no term.open(), no onData handler. The
  // session connects from a separate effect that needs no host, so the sheet showed
  // "Connected" over a blank pane that swallowed every keystroke (reported 2026-09-26 on
  // JH-MASTER-DESK). Tracking the host in STATE re-runs this the moment the node exists.
  useEffect(() => {
    if (!open || !host) return;

    const mono = getComputedStyle(document.documentElement).getPropertyValue("--md-font-mono").trim();
    const term = new Terminal({
      cursorBlink: true,
      fontFamily: mono || "ui-monospace, monospace",
      fontSize: 14,
      lineHeight: 1.15,
      scrollback: 5000,
      theme: themeFromTokens(),
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host);
    termRef.current = term;
    // onReady fires from the session effect and may have already run while termRef was still
    // null (its focus() then did nothing), so focus here too.
    term.focus();

    const refit = () => {
      try {
        fit.fit();
      } catch {
        /* host not laid out yet */
      }
    };
    refit();

    const dataSub = term.onData((data) => sessionRef.current.sendInput(data));
    const resizeSub = term.onResize(({ cols, rows }) => sessionRef.current.resize(cols, rows));
    sessionRef.current.resize(term.cols, term.rows);

    // Tablet rotation, sheet resize, split view: keep cols/rows in step with the well.
    const ro = new ResizeObserver(refit);
    ro.observe(host);

    // Theme flips (explicit toggle or OS change) repaint the terminal from the tokens.
    const retheme = () => {
      term.options.theme = themeFromTokens();
    };
    const mo = new MutationObserver(retheme);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", retheme);

    return () => {
      mq.removeEventListener("change", retheme);
      mo.disconnect();
      ro.disconnect();
      dataSub.dispose();
      resizeSub.dispose();
      term.dispose();
      termRef.current = null;
    };
  }, [open, host]);

  // --- session lifecycle: open the shell with the sheet, close it with the sheet ----------
  useEffect(() => {
    if (!open) return;
    void sessionRef.current.connect();
    return () => sessionRef.current.disconnect();
  }, [open, device.serial]);

  const reconnect = () => {
    termRef.current?.reset();
    void session.connect();
  };

  const { status } = session;
  const pill = PHASE_PILL[status.phase];
  const active = ["requesting", "connecting", "waiting_device", "ready"].includes(status.phase);
  const title = device.name || device.serial;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/50" />
        <Dialog.Content
          aria-describedby={undefined}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed inset-0 z-sheet flex flex-col bg-overlay shadow-sheet outline-none lg:inset-6 lg:rounded-lg lg:border lg:border-line"
        >
          {/* Header: identity on the left, live state + controls on the right. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium uppercase tracking-wider text-ink-subtle">
                Terminal{device.kind && KIND_LABEL[device.kind] ? ` · ${KIND_LABEL[device.kind]}` : ""}
              </div>
              <Dialog.Title className="truncate text-lg font-semibold text-ink">
                {title}
                {title !== device.serial && (
                  <span className="ml-2 font-mono text-sm font-normal text-ink-muted">{device.serial}</span>
                )}
              </Dialog.Title>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill status={pill.ramp} className={active && status.phase !== "ready" ? "animate-pulse" : undefined}>
                {pill.label}
              </StatusPill>
              {active ? (
                <Button variant="secondary" onClick={() => session.disconnect()}>
                  Disconnect
                </Button>
              ) : (
                <Button variant="primary" onClick={reconnect}>
                  {status.phase === "idle" ? "Connect" : "Reconnect"}
                </Button>
              )}
              <Dialog.Close asChild>
                <Button variant="ghost" aria-label="Close terminal">
                  ✕
                </Button>
              </Dialog.Close>
            </div>
          </div>

          {/* The well. xterm owns this box; the overlay covers it whenever there is no shell. */}
          <div className="relative min-h-0 flex-1 p-2 sm:p-3">
            <div className="relative h-full overflow-hidden rounded border border-line bg-surface-2">
              <div ref={setHost} className="absolute inset-0 p-2" />
              {status.phase !== "ready" && (
                <StateOverlay status={status} deviceName={title} onReconnect={reconnect} />
              )}
            </div>
          </div>

          <SessionFooter status={status} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Covers the well with what is happening (or why nothing can happen). */
function StateOverlay({
  status,
  deviceName,
  onReconnect,
}: {
  status: TerminalStatus;
  deviceName: string;
  onReconnect: () => void;
}) {
  const progress: Partial<Record<TerminalPhase, { title: string; detail: string }>> = {
    idle: { title: "Not connected", detail: "Connect to open a shell on this device." },
    requesting: {
      title: `Asking owgw to start RTTY on ${deviceName}`,
      detail: "The device has up to a minute to acknowledge.",
    },
    connecting: { title: "Opening the relay", detail: "The controller is attaching to owgw's RTTY service." },
    waiting_device: {
      title: "Device acknowledged",
      detail: "Waiting for it to dial back to the RTTY server.",
    },
  };
  const inProgress = progress[status.phase];
  const ended = status.phase === "failed" || status.phase === "closed";
  const hint = status.code ? HINTS[status.code] : undefined;

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-surface-2/90 p-4">
      <div
        className="w-full max-w-md"
        role={status.phase === "failed" ? "alert" : "status"}
        aria-live="polite"
      >
        {inProgress && (
          <>
            <div className="mb-3 flex gap-1.5" aria-hidden>
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-1.5 w-6 animate-pulse rounded-full bg-ink-subtle"
                  style={{ animationDelay: `${i * 180}ms` }}
                />
              ))}
            </div>
            <div className="text-base font-medium text-ink">{inProgress.title}</div>
            <div className="mt-1 text-sm text-ink-muted">{inProgress.detail}</div>
          </>
        )}
        {ended && (
          <>
            <div
              className={
                status.phase === "failed"
                  ? "border-l-2 border-critical pl-3"
                  : "border-l-2 border-line pl-3"
              }
            >
              <div className="text-base font-medium text-ink">
                {status.phase === "failed" ? "Terminal unavailable" : "Session ended"}
              </div>
              <div className="mt-1 text-sm text-ink">{status.message}</div>
              {hint && <div className="mt-2 text-sm text-ink-muted">{hint}</div>}
              {status.code && <div className="mt-2 font-mono text-xs text-ink-subtle">{status.code}</div>}
            </div>
            <div className="mt-4">
              <Button variant="primary" onClick={onReconnect}>
                Reconnect
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Session limits + a running clock, and the audit promise (what is and is not recorded). */
function SessionFooter({ status }: { status: TerminalStatus }) {
  const [now, setNow] = useState(() => Date.now());
  const running = status.phase === "ready" && status.readyAt !== undefined;

  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  const limits = status.limits;
  const elapsed = running ? (now - (status.readyAt as number)) / 1000 : 0;
  const fraction = running && limits ? Math.min(1, elapsed / limits.maxSessionS) : 0;

  return (
    <div className="border-t border-line">
      {/* Hairline session-limit gauge: neutral ink, it is information, not an affordance. */}
      <div className="h-px bg-line">
        <div className="h-px bg-ink-subtle transition-[width] duration-1000" style={{ width: `${fraction * 100}%` }} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2 text-xs text-ink-muted">
        <span>
          RTTY via owgw
          {limits && (
            <>
              {" · "}idle closes after {Math.round(limits.idleTimeoutS / 60)} min{" · "}session limit{" "}
              {Math.round(limits.maxSessionS / 60)} min
            </>
          )}
        </span>
        {/* The uCentral rtty handler starts /bin/login (no auto-login), so the prompt is the
            device's own -- verified live on an EAP104 2026-09-13. Say so before someone types a
            command into the username field. */}
        <span className="text-ink-subtle">
          Sign in with the device's local credentials. Keystrokes are not recorded; opening and closing is audited.
        </span>
        {running && limits && (
          <span className="font-mono tabular-nums text-ink">
            {fmtClock(elapsed)} / {fmtClock(limits.maxSessionS)}
          </span>
        )}
      </div>
    </div>
  );
}
